import { SyntheticEvent, useCallback } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { SMSNumberV1 } from '@frontend/api-sms-number';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { ContactActionPrefixes } from '@frontend/tracking-prefixes';
import { ComponentProps } from '@frontend/types';
import { ModalControlTriggerProps, useAlert, useModalControl } from '@frontend/design-system';
import { ReviewActionModal } from '../components';
import { ReviewActionFlowProps as ReviewActionFlowProps } from '../types';

type UseReviewActionArgs = ReviewActionFlowProps & {
  onClose?: () => void;
};

type UseReviewActionResult =
  | {
      modalProps: ComponentProps<typeof ReviewActionModal>;
      triggerProps: ModalControlTriggerProps & {
        label: string;
        trackingId: string;
      };
      isValidatingPhone: boolean;
    }
  | undefined;

/**
 * Hook to use the review invitation flow. This should be used in combination with the `ReviewActionModal`
 * @returns The props necessary for the `ReviewActionModal` and trigger props for wherever this action is
 * triggered. Returns `undefined` if the user does not have the necessary permissions for reviews.
 */
export const useReviewAction = ({
  groupId,
  personId,
  appointmentId,
  onClose,
}: UseReviewActionArgs): UseReviewActionResult => {
  const { t } = useTranslation('contact-actions');
  const { error: alertError } = useAlert();
  const modalControl = useModalControl({
    onClose,
  });
  const hasACL = hasSchemaACL(groupId, Permission.REVIEW_READ);
  const reviewsCustomization = CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
    locationIds: [groupId],
    customizationFlag: Feature.REVIEWS_HOME_ICON,
    activeStates: [CustomizationFlagTypes.CustomizationFlagState.ACTIVE],
  }).isFeatureActiveInAllLocations;

  const { validPhones, isLoading: isValidatingPhone } = SMSNumberV1.Hooks.usePersonSMSNumbersValidity({
    personId,
    groupIds: [groupId],
    defaultToValid: false,
  });

  const handleTriggerClick = useCallback(
    (e?: SyntheticEvent) => {
      if (validPhones.length === 0) {
        e?.preventDefault();
        e?.stopPropagation();
        alertError(t('The selected contact has no textable phone numbers.'));
        return;
      }
      modalControl.triggerProps.onClick?.(e);
    },
    [validPhones.length, alertError, t, modalControl.triggerProps]
  );

  if (!hasACL || !reviewsCustomization) return undefined;

  return {
    modalProps: {
      groupId,
      personId,
      appointmentId,
      ...modalControl.modalProps,
    },
    triggerProps: {
      ...modalControl.triggerProps,
      onClick: handleTriggerClick,
      label: t('Request Review'),
      trackingId: `${ContactActionPrefixes.ReviewInvitation}`,
    },
    isValidatingPhone,
  };
};
